import React from 'react';
import IconButton from '@mui/material/IconButton';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { t } from 'i18next';

const STATUS_COLORS: Record<string, string> = {
  active: 'var(--color-success)',
  on_hold: 'var(--color-warning)',
  completed: 'var(--color-info)',
  archived: 'var(--text-color-calendar-week)',
};

const STATUS_BG_COLORS: Record<string, string> = {
  active: 'var(--color-success-bg)',
  on_hold: 'var(--color-warning-bg)',
  completed: 'var(--color-info-bg)',
  archived: 'var(--btn-user-in)',
};

const STATUS_LABELS: Record<string, string> = {
  active: 'Active', on_hold: 'On Hold', completed: 'Completed', archived: 'Archived',
};

interface Props {
  project: any;
  taskCount?: number;
  eventCount?: number;
  onClick: () => void;
  onEdit: (e: React.MouseEvent) => void;
  onDelete: (e: React.MouseEvent) => void;
}

export default function CrmProjectCard({ project, taskCount = 0, eventCount = 0, onClick, onEdit, onDelete }: Props) {
  const color = project.color ?? 'var(--color-info)';
  const statusColor = STATUS_COLORS[project.status] ?? 'var(--text-color-calendar-week)';
  const statusBgColor = STATUS_BG_COLORS[project.status] ?? 'var(--btn-user-in)';
  const statusLabel = STATUS_LABELS[project.status] ?? project.status;
  const manager = project.manager
    ? `${project.manager.firstName ?? ''} ${project.manager.lastName ?? ''}`.trim()
    : null;

  const dateRange = (() => {
    const s = project.startDate ? String(project.startDate).slice(0, 10) : null;
    const e = project.endDate ? String(project.endDate).slice(0, 10) : null;
    if (s && e) return `${s} – ${e}`;
    if (s) return `From ${s}`;
    if (e) return `Until ${e}`;
    return null;
  })();

  return (
    <div
      onClick={onClick}
      className="rounded-[5px] border cursor-pointer transition-all hover:shadow-md flex flex-col"
      style={{ borderColor: 'var(--border-input)', backgroundColor: 'var(--bg-form)' }}>

      {/* colour strip */}
      <div className="h-[4px] rounded-t-[5px]" style={{ backgroundColor: color }} />

      <div className="p-[14px] flex flex-col flex-1">
        {/* header */}
        <div className="flex items-start justify-between gap-[6px] mb-[8px]">
          <div className="flex items-center gap-[8px] min-w-0">
            <span className="w-[10px] h-[10px] rounded-full flex-shrink-0" style={{ backgroundColor: color }} />
            <span className="text-[14px] font-semibold text-[var(--text-color)] truncate">{project.name}</span>
          </div>
          <span className="flex-shrink-0 text-[11px] font-medium px-[8px] py-[2px] rounded-full"
            style={{ backgroundColor: statusBgColor, color: statusColor }}>
            {statusLabel}
          </span>
        </div>

        {/* meta */}
        <div className="flex flex-col gap-[4px] flex-1 text-[12px] text-[var(--text-color-calendar-week)]">
          {project.type && (
            <span className="inline-flex w-fit px-[7px] py-[1px] rounded-full border border-[var(--border-input)] text-[11px]">
              {project.type}
            </span>
          )}
          {manager && (
            <div className="flex items-center gap-[5px] mt-[2px]">
              <span className="opacity-60">👤</span>
              <span>{manager}</span>
            </div>
          )}
          {project.organisation && (
            <div className="flex items-center gap-[5px]">
              <span className="opacity-60">🏢</span>
              <span className="truncate">{project.organisation.name}</span>
            </div>
          )}
          {dateRange && (
            <div className="flex items-center gap-[5px]">
              <span className="opacity-60">📅</span>
              <span>{dateRange}</span>
            </div>
          )}
        </div>

        {/* counters + actions */}
        <div className="flex items-center justify-between mt-[10px] pt-[8px]" style={{ borderTop: 'var(--border-input)' }}>
          <div className="flex gap-[12px] text-[12px] text-[var(--text-color-calendar-week)]">
            <span>✅ {taskCount} {t('Tasks') || 'tasks'}</span>
            <span>📌 {eventCount} {t('Events') || 'events'}</span>
          </div>
          <div className="flex gap-[2px]" onClick={(e) => e.stopPropagation()}>
            <IconButton size="small" sx={{ color: 'var(--text-color)', opacity: 0.5 }} onClick={onEdit}>
              <EditIcon sx={{ fontSize: 15 }} />
            </IconButton>
            <IconButton size="small" sx={{ color: 'var(--text-color)', opacity: 0.5 }} onClick={onDelete}>
              <DeleteIcon sx={{ fontSize: 15 }} />
            </IconButton>
          </div>
        </div>
      </div>
    </div>
  );
}
